/**
 * Popper filtra kolumny – prosta reguła (operator + wartość), wiele warunków.
 * Zapis w tym samym stanie co AdvancedFiltersDialog (filterRuleGroups w hooku).
 */

import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Popper,
  Paper,
  IconButton,
  Button as ButtonMui,
  Box,
  Typography
} from '@mui/material';
import { X, Trash, Plus } from 'react-bootstrap-icons';
import WiseSelect from 'components/wise/WiseSelect';
import FilterValueCell from 'components/wise/WiseTable/components/FilterValueCell';
import { TWiseTableAdvancedFilterOperator } from 'components/wise/types';

import { useWiseTable } from 'components/wise/WiseTable/context';

import styles from 'theme/components/wise/WiseTable/components/ColumnFilterPopper/ColumnFilterPopper.module.scss';

export interface IColumnFilterRow {
  operator: TWiseTableAdvancedFilterOperator | null;
  value: string;
  connectorAfter?: 'and' | 'or';
}

const getEmptyRow = (): IColumnFilterRow => ({
  operator: null,
  value: '',
  connectorAfter: 'and'
});

const ColumnFilterPopper: FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<IColumnFilterRow[]>([getEmptyRow()]);
  const { advancedFilters } = useWiseTable();

  const {
    columnFilterPopperOpen: open,
    onCloseColumnFilterDialog: onClose,
    columnFilterPopperAnchorEl: anchorEl,
    columnFilterPopperColumnId: columnId,
    getColumnLabel,
    operatorOptions,
    connectorOptions,
    getFilterByFieldSymbol,
    applyColumnFilter: onApply,
    getColumnFilterRowsForColumn
  } = advancedFilters;

  const columnLabel = getColumnLabel(advancedFilters.columnFilterPopperColumnId ?? '');
  const initialRows = columnId
    ? getColumnFilterRowsForColumn(columnId)
    : undefined;

  useEffect(() => {
    if (open) {
      const fromRedux =
        initialRows && initialRows.length > 0 ? initialRows : [getEmptyRow()];
      setRows(fromRedux);
    }
  }, [open, columnId]);

  const addCondition = (afterIndex?: number) => {
    if (afterIndex != null) {
      setRows((prev) => [
        ...prev.slice(0, afterIndex + 1),
        getEmptyRow(),
        ...prev.slice(afterIndex + 1)
      ]);
    } else {
      setRows((prev) => [...prev, getEmptyRow()]);
    }
  };

  const removeRow = (index: number) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const updateRow = (index: number, patch: Partial<IColumnFilterRow>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const handleApply = () => {
    if (columnId && rows.length > 0) {
      onApply(columnId, rows);
      onClose();
    }
  };

  const handleClear = () => {
    if (columnId) {
      onApply(columnId, []);
      onClose();
    }
  };

  if (!columnId) return null;

  const filter = getFilterByFieldSymbol(columnId);

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'flip', options: { fallbackPlacements: ['bottom-end', 'top-start'] } }
      ]}
      sx={{ zIndex: 1300 }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 2,
          minWidth: 360,
          maxWidth: 480,
          maxHeight: 500,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexShrink: 0 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('Filtr kolumny')}: {columnLabel}
            </Typography>
            <IconButton aria-label={t('Zamknij')} onClick={onClose} size="small">
              <X size={20} />
            </IconButton>
          </Box>
          <div className={styles.content}>
            <div className={styles.expressionsList}>
              {rows.map((row, index) => (
              <React.Fragment key={index}>
                <div className={styles.expressionRow}>
                  <div className={styles.cell}>
                    <WiseSelect<{ value: TWiseTableAdvancedFilterOperator }>
                      styleGroup="portal"
                      mode="singlechoice"
                      placeholder={t('Operator')}
                      options={operatorOptions}
                      value={row.operator}
                      onChange={(item) => updateRow(index, { operator: item?.value ?? null })}
                      clearable
                      optionFontSize="14px"
                    />
                  </div>
                  <div className={styles.cellFlex}>
                    <FilterValueCell
                      filter={filter}
                      value={row.value}
                      onChange={(v) => updateRow(index, { value: v })}
                    />
                  </div>
                  <IconButton
                    className={styles.removeRowButton}
                    onClick={() => addCondition(index)}
                    aria-label={t('Dodaj warunek')}
                    size="small">
                    <Plus size={18} />
                  </IconButton>
                  <IconButton
                    className={styles.removeRowButton}
                    onClick={() => removeRow(index)}
                    disabled={rows.length <= 1}
                    aria-label={t('Usuń warunek')}
                    size="small">
                    <Trash size={18} />
                  </IconButton>
                </div>
                {index < rows.length - 1 && (
                  <div className={styles.connectorRow}>
                    <WiseSelect<{ value: 'and' | 'or' }>
                      styleGroup="portal"
                      mode="singlechoice"
                      placeholder={t('Oraz')}
                      options={connectorOptions}
                      value={row.connectorAfter ?? 'and'}
                      onChange={(item) =>
                        updateRow(index, { connectorAfter: item?.value ?? 'and' })
                      }
                      optionFontSize="14px"
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
            </div>
          </div>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider'
          }}>
          <ButtonMui color="secondary" size="small" onClick={handleClear}>
            {t('Wyczyść')}
          </ButtonMui>
          <ButtonMui variant="contained" color="secondary" size="small" onClick={handleApply}>
            {t('Zastosuj')}
          </ButtonMui>
        </Box>
      </Paper>
    </Popper>
  );
};

export default ColumnFilterPopper;
